import { ReactElement, useState, useEffect } from "react";
import { Layout, Button, Row, Col, Card, Table, Tabs, Image,Space,Tooltip, Popconfirm } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { EditOutlined, DeleteOutlined, ShareAltOutlined, ScheduleOutlined, PartitionOutlined, PlusOutlined, CheckOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "firebase/firestore";
import { getAuthState, getProductState, getCategoryState } from "../../redux/selectors";
import { CreateProductDrawer } from "./CreateProductDrawer";
import { CreateCategoryDrawer } from "./CreateCategoryDrawer";
import { EditCategoryDrawer } from "./EditCategoryDrawer";
import { EditProductDrawer } from "./EditProductDrawer";

import { ContentHeader, RightButton, BreadCrumbWrapper } from "../../components/GlobalStyles";
import {SET_LOADING_STATE, RESET_LOADING_STATE,REMOVE_CATEGORY_REQUEST, REMOVE_PRODUCT_REQUEST, FETCH_ALL_PRODUCTS_REQUEST,FETCH_ALL_CATEGORY_REQUEST } from "../../redux/constants";

const { Content } = Layout;
const { TabPane } = Tabs;

export function MarketManagement(): ReactElement {
    const [createProductDrawerVisible, setCreateProductDrawerVisible] = useState(false);
    const [createCategoryDrawerVisible,setCreateCategoryDrawerVisible] = useState(false);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    
    const { currentUser } = useSelector(getAuthState);
    const { products } = useSelector(getProductState);
    const { categories } = useSelector(getCategoryState);
    const [selectedCategory, setSelectedCategory] = useState<any>();
    const [editCategoryDrawerVisible, setEditCategoryDrawerVisible] = useState(false);
    const [selectedEditProduct, setSelectedEditProduct] =  useState<any>();
    const [editProductStatus, setEditProductStatus] = useState<boolean>(false);

    useEffect(() => {
        if(currentUser)
        {
            dispatch({ type: FETCH_ALL_PRODUCTS_REQUEST, payload: { currentUser } });
            dispatch({ type: FETCH_ALL_CATEGORY_REQUEST, payload: { currentUser } });
        }

    }, [currentUser]);

    const onEdit = (record:any) => {
        setEditCategoryDrawerVisible(true);
        setSelectedCategory(record);
    }

    const onEditProduct = (record:any) => {
        console.log(record);
        setEditProductStatus(true);
        setSelectedEditProduct(record);
    }

    const onDeleteProduct = (record:any) => {
        const formValue = {
            currentId : record.product_id
        }
        dispatch({ type: REMOVE_PRODUCT_REQUEST, payload: formValue });
        dispatch({ type: FETCH_ALL_PRODUCTS_REQUEST, payload: { currentUser } });
    }

    const onDeleteCategory = (record:any) => {
        const formValue = {
            currentId : record.catID
        }
        dispatch({ type: REMOVE_CATEGORY_REQUEST, payload: formValue });
        dispatch({ type: FETCH_ALL_CATEGORY_REQUEST, payload: { currentUser } });
    }

    const columns: any = [
        {
            title: t("table.image"),
            key: "logo",
            render: ({photo}: any) => <Image src={photo} width={80} height={80} />,
        },
        {
            title: t("table.id"),
            dataIndex: "product_id",
            key: "product_id",
        },
        {
          title: t("table.name"),
          dataIndex: "name",
          key: "name",
        },
        {
            title: t("table.price"),
            dataIndex: "cost",
            key: "cost",
        },
        {
            title: t("table.condition"),
            dataIndex: "condition",
            key: "condition",
        },
        {
            title: t("table.quantity"),
            dataIndex: "quantity",
            key: "quantity",
        },
        {
            title: t("table.tags"),
            dataIndex: "tags",
            key: "tags",
        },
        {
            title: t("table.description"),
            dataIndex: "description",
            key: "description",
        },
        {
            title: t("market.product_shipping"),
            dataIndex: "shipping",
            key: "shipping",
        },
        {
            title: t("market.product_visible"),
            dataIndex: "visibility",
            key: "visibility",
        },
        {
            title: t("table.category"),
            dataIndex: "changedCategory",
            key: "category",
        },
        {
            title: t("table.subCategory"),
            dataIndex: "changedSubcategory",
            key: "subcategory",
        },
        {
            title: t("common.action"),
            key: "action",
            render: (record: any) => (
                <Space size="middle">
                    <Tooltip placement="top" title="Edit Product">
                        <Button type="primary" shape="circle" onClick={() => onEditProduct(record)}><EditOutlined /></Button>
                    </Tooltip>
                    
                    <Popconfirm
                        title="Are you sure to delete this product?"
                        onConfirm={()=>onDeleteProduct(record)}
                        okText="Yes"
                        cancelText="No"
                    >
                            <Button danger type="primary" shape="circle" ><DeleteOutlined /></Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const categoryColumn: any = [
        {
            title: t("table.cat_id"),
            dataIndex: "catID",
            key: "catID",
        },
        {
            title: t("table.name"),
            dataIndex: "name",
            key: "name",
        },
        {
            title: t("table.keyword"),
            dataIndex: "keyword",
            key: "keyword",
        },
        {
            title: t("table.parent_cat"),
            dataIndex: "parent",
            key: "parent",
        },
        {
            title: t("common.action"),
            key: "action",
            render: (record: any) => (
                <Space size="middle">
                    <Button shape="circle" type="primary" onClick={() => onEdit(record)}><EditOutlined /></Button>
                    <Popconfirm
                        title="Are you sure to delete this category?"
                        onConfirm={()=>onDeleteCategory(record)}
                        okText="Yes"
                        cancelText="No"
                    >
                            <Button danger type="primary" shape="circle" ><DeleteOutlined /></Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];
    
  return (
    <Content>
        <ContentHeader>{t("market.market_label")}</ContentHeader>
        <BreadCrumbWrapper>
            <RightButton
                type="primary"
                onClick={() => setCreateProductDrawerVisible(true)}
                icon={ <PlusOutlined /> }
            >
                {t("market.create_market")}
            </RightButton>
            { 
                currentUser && currentUser.role === "admin" && 
                <Button
                    style = {{float:"right",right:180,position:"absolute"}}
                    danger
                    type="primary"
                    onClick={() => setCreateCategoryDrawerVisible(true)}
                    icon={ <PlusOutlined /> }
                >
                    {t("common.create_category")}
                </Button>
            }
        </BreadCrumbWrapper>
        { createProductDrawerVisible && <CreateProductDrawer
            visible={createProductDrawerVisible}
            onClose={() => setCreateProductDrawerVisible(false)}
        /> }
        { createCategoryDrawerVisible && <CreateCategoryDrawer
            visible={createCategoryDrawerVisible}
            onClose={() => setCreateCategoryDrawerVisible(false)}
        /> }
        <Row gutter={{ sm: 8, md: 16}}>
            <Col span={24} style={{ marginTop : 10}}>
                <Tabs defaultActiveKey="1" style={{backgroundColor:"white",padding:20}}>
                    <TabPane tab="Products" key="1">
                        <Table columns={columns} dataSource={products} />
                    </TabPane>
                    { 
                        currentUser && currentUser.role === "admin" && categories && 
                        <TabPane tab="Category" key="2">
                            <Table columns={categoryColumn} dataSource={categories} />
                        </TabPane>
                    }
                    {/* <TabPane tab="Transaction" key="3">
                    </TabPane> */}
                </Tabs>
            </Col>
        </Row>
        {selectedCategory && (
            <EditCategoryDrawer
                visible={editCategoryDrawerVisible}
                onClose={() => {
                    setSelectedCategory(null);
                    setEditCategoryDrawerVisible(false);
                }}
                cat={selectedCategory}
            />
        )}

        {editProductStatus && (
            <EditProductDrawer
                visible={editProductStatus}
                onClose={() => {
                    setSelectedEditProduct(null);
                    setEditProductStatus(false);
                }}
                pro={selectedEditProduct}
            />
        )}
    </Content>
  );
}
